import { h, JSX } from 'preact';
import ObservingComponent from '../componentBases/observingComponent';
import RoomStatusDisplayProps from './roomStatusDisplayProps';
import Modal from '../containers/modal';
import Button from '../buttons/button';
import ButtonColors from '../buttons/buttonColors';
import Alert from '../alerts/alert';
import Field from '../fields/field';
import FormattedDate from '../text/formattedDate';
import { DateFormats, DateUtils, MeetingDisplayItem, RoomStatusDisplayOrientation, StringUtils } from 'core.frontend';

class RoomStatusDisplayPage extends ObservingComponent<RoomStatusDisplayProps> {

    private readonly gridColumnWidth = 100;
    private readonly gridColumnMinutes = 30;

    public componentWillMount(): void {
        this.registerUpdateObserver(this.props.page.observationProvider);
    }

    private getGridDurationMinutes = () => {
        return this.props.page.gridTimes.length * this.gridColumnMinutes;
    }

    private getVisibleMeetingStartMinutes = (meeting: MeetingDisplayItem) => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(meeting.startDateTime, this.props.page.firstGridDateTime);

        return Math.max(0, minutesFromGridStart);
    }

    private getVisibleMeetingEndMinutes = (meeting: MeetingDisplayItem) => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(meeting.endDateTime, this.props.page.firstGridDateTime);

        return Math.min(this.getGridDurationMinutes(), minutesFromGridStart);
    }

    private isMeetingVisibleOnGrid = (meeting: MeetingDisplayItem) => {
        return this.getVisibleMeetingEndMinutes(meeting) > this.getVisibleMeetingStartMinutes(meeting);
    }

    private getCurrentTimeLineLeft = () => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(this.props.page.meetingDisplayData.currentDateTimeOffset, this.props.page.firstGridDateTime);

        return minutesFromGridStart / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private isCurrentTimeVisibleOnGrid = () => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(this.props.page.meetingDisplayData.currentDateTimeOffset, this.props.page.firstGridDateTime);

        return minutesFromGridStart >= 0 && minutesFromGridStart <= this.getGridDurationMinutes();
    }

    private getMeetingBlockWidth = (meeting: MeetingDisplayItem) => {
        const lengthInMinutes = this.getVisibleMeetingEndMinutes(meeting) - this.getVisibleMeetingStartMinutes(meeting);

        return lengthInMinutes / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private getMeetingBlockLeft = (meeting: MeetingDisplayItem) => {
        const minutesFromGridStart = this.getVisibleMeetingStartMinutes(meeting);

        return minutesFromGridStart / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private renderMeetingContent = (meeting: MeetingDisplayItem): JSX.Element => {
        const { page } = this.props;

        return (
            <span class="meeting-display-meeting-content">
                {page.form.model.displayMeetingName && meeting.meetingName &&
                    <span>
                        {StringUtils.truncate(meeting.meetingName, 30)}

                        {page.form.model.displayMeetingNumber &&
                            <span> ({meeting.meetingNumber})</span>
                        }
                    </span>
                }

                {page.form.model.displayMeetingNumber && !(page.form.model.displayMeetingName && meeting.meetingName) &&
                    <span>{meeting.meetingNumber}</span>
                }

                {page.form.model.displayMeetingTime &&
                    <span>
                        <FormattedDate
                            format={DateFormats.hhCmm}
                            date={meeting.startDateTime} />

                        <span> - </span>

                        <FormattedDate
                            format={DateFormats.hhCmm}
                            date={meeting.endDateTime} />
                    </span>
                }

                {page.form.model.displayMeetingPoc && meeting.pocName &&
                    <span>POC: {meeting.pocName}</span>
                }
            </span>
        );
    }

    private renderMeetingListHeaders = (): JSX.Element[] => {
        const { page } = this.props;
        const headers = [];

        if (page.form.model.displayMeetingName) {
            headers.push(<th>Meeting Name</th>);
        }

        if (page.form.model.displayMeetingNumber) {
            headers.push(<th class="fit-content">Meeting #</th>);
        }

        if (page.form.model.displayMeetingTime) {
            headers.push(<th class="fit-content">Time</th>);
        }

        headers.push(<th class="text-center">Room</th>);

        if (page.form.model.displayMeetingPoc) {
            headers.push(<th class="text-center">POC</th>);
        }

        return headers;
    }

    private renderMeetingListCells = (meeting: MeetingDisplayItem): JSX.Element[] => {
        const { page } = this.props;
        const cells = [];

        if (page.form.model.displayMeetingName) {
            cells.push(<td>{meeting.meetingName}</td>);
        }

        if (page.form.model.displayMeetingNumber) {
            cells.push(<td class="text-center fit-content">{meeting.meetingNumber}</td>);
        }

        if (page.form.model.displayMeetingTime) {
            cells.push(
                <td class="text-center fit-content">
                    <FormattedDate
                        format={DateFormats.hhCmm}
                        date={meeting.startDateTime} />

                    <span> - </span>

                    <FormattedDate
                        format={DateFormats.hhCmm}
                        date={meeting.endDateTime} />
                </td>
            );
        }

        cells.push(<td class="text-center">{meeting.venueName}</td>);

        if (page.form.model.displayMeetingPoc) {
            cells.push(<td class="text-center">{meeting.pocName}</td>);
        }

        return cells;
    }

    public render({ page }: RoomStatusDisplayProps): JSX.Element {
        const isVertical = page.form.model.orientation === RoomStatusDisplayOrientation.Vertical;

        return (
            <div class="display-page-wrapper">
                <Alert
                    className="mb-0"
                    alert={page.alert} />

                {page.settingsVisible &&
                    <Modal
                        heading="Room Status Display Settings"
                        close={() => {}}
                        hideBackButton={true}
                        footerRightButtons={[
                            <Button
                                color={ButtonColors.Primary}
                                button={page.submitButton} />
                        ]}>

                        <div class="form-container room-status-display-settings">
                            <p>
                                <b>Note: </b> Only finalized meetings will be displayed. Refresh the page to access these settings again.
                            </p>

                            <h4 class="section-header">Display Options</h4>

                            {/* <div class="room-status-settings-row">
                                <b>Orientation:</b>

                                <label class="room-status-orientation-option">
                                    <span class="orientation-icon horizontal"></span>
                                    <span>Horizontal</span>
                                    <input
                                        type="radio"
                                        name="room-status-orientation"
                                        checked={!isVertical}
                                        onChange={() => page.setOrientation(RoomStatusDisplayOrientation.Horizontal)} />
                                </label>

                                <label class="room-status-orientation-option">
                                    <span class="orientation-icon vertical"></span>
                                    <span>Vertical</span>
                                    <input
                                        type="radio"
                                        name="room-status-orientation"
                                        checked={isVertical}
                                        onChange={() => page.setOrientation(RoomStatusDisplayOrientation.Vertical)} />
                                </label>
                            </div> */}

                            <div class="room-status-settings-row">
                                <b>Options:</b>

                                <Field
                                    className="room-status-option-checkbox"
                                    field={page.form.fields[0]} />

                                <Field
                                    className="room-status-option-checkbox"
                                    field={page.form.fields[1]} />
                            </div>

                            <div class="room-status-settings-row">
                                <b>Content:</b>

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[2]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[3]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[4]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[5]} />
                            </div>

                            <div class="room-status-venue-header">
                                <b>Venues</b>
                                <button
                                    type="button"
                                    onClick={page.toggleAllVenues}>Toggle All</button>
                            </div>

                            <div class="room-status-venue-options">
                                {page.venueFields.map((field) =>
                                    <Field
                                        className="room-status-option-checkbox"
                                        field={field} />
                                )}
                            </div>
                        </div>
                    </Modal>
                }

                {!page.settingsVisible &&
                    <div class={'meeting-display ' + (isVertical ? 'vertical' : 'horizontal')}>
                        <div class="meeting-display-header">
                            <div>{page.meetingDisplayData.eventName}</div>

                            <FormattedDate
                                className="meeting-display-date"
                                date={page.meetingDisplayData.currentDateTimeOffset}
                                format={DateFormats.MMMdYYYY} />

                            <FormattedDate
                                className="meeting-display-time"
                                date={page.meetingDisplayData.currentDateTimeOffset}
                                format={DateFormats.hhCmm}/>
                        </div>
                        <div class="meeting-display-content">

                            {page.form.model.displayMeetingList &&
                                <div class={'meeting-display-list ' + (!page.form.model.displayMeetingGrid ? 'full' : '')}>
                                    <table>
                                        <thead>
                                            {this.renderMeetingListHeaders()}
                                        </thead>
                                        <tbody>
                                            {page.selectedMeetings.map((meeting) =>
                                                <tr>
                                                    {this.renderMeetingListCells(meeting)}
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            }

                            {page.form.model.displayMeetingGrid &&
                                <div class="meeting-display-grid">
                                    <table>
                                        <thead>
                                            <th></th>
                                            {page.gridTimes.map((time) =>
                                                <th class="meeting-display-grid-time">
                                                    <b>{time}</b>
                                                </th>
                                            )}
                                        </thead>
                                        <tbody>
                                            {page.selectedVenues.map((venue) =>
                                                <tr>
                                                    <td>
                                                        <b>{venue.name}</b>
                                                    </td>

                                                    <td
                                                        class="meeting-container-cell"
                                                        colSpan={100}>

                                                        {this.isCurrentTimeVisibleOnGrid() &&
                                                            <span
                                                                class="current-time-line"
                                                                style={{
                                                                    'left': this.getCurrentTimeLineLeft() + 'px'
                                                                }}>
                                                            </span>
                                                        }

                                                        {page.selectedMeetings.map((meeting) => {

                                                            if (meeting.venueId !== venue.id) {
                                                                return;
                                                            }

                                                            if (!this.isMeetingVisibleOnGrid(meeting)) {
                                                                return;
                                                            }

                                                            return (
                                                                <span
                                                                    class="meeting-block"
                                                                    style={{
                                                                        'left': this.getMeetingBlockLeft(meeting) + 'px',
                                                                        'width': this.getMeetingBlockWidth(meeting) + 'px'
                                                                    }}>

                                                                    {this.renderMeetingContent(meeting)}
                                                                </span>
                                                            );
                                                        })}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        );
    }
}

export default RoomStatusDisplayPage;
